import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StateSelect } from "@/components/ui/state-select";
import { MaskedInput } from "@/components/ui/masked-input";
import { PhoneField } from "@/components/PhoneField";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/lib/api-error";
import { toastApiError, userErrorMessage } from "@/lib/error-messages";
import { createContractor } from "@/requests/internal-contractors/create-contractor";
import { lookupHajocaCustomer } from "@/requests/internal-contractors/lookup-hajoca-customer";
import { parsePhoneNumber } from "libphonenumber-js";
import type { CreateContractorRequest, CreateContractorResult } from "@onsemble/shared/types/internal";

const createContractorSchema = z.object({
  orgName: z.string().min(1, "Organization name is required"),
  hajocaCustomerId: z.string().min(1, "Hajoca Customer ID is required"),
  hajocaProfitCenter: z.string().min(1, "Profit Center is required"),
  phone: z.string().optional(),
  address: z
    .object({
      street: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().default("US"),
      zipCode: z.string().optional(),
    })
    .optional(),
});

type CreateContractorFormData = z.infer<typeof createContractorSchema>;

interface CreateContractorFormProps {
  onSuccess?: () => void;
}

export function OnboardingForm({ onSuccess }: CreateContractorFormProps = {}) {
  const { toast } = useToast();

  const form = useForm<CreateContractorFormData>({
    resolver: zodResolver(createContractorSchema),
    defaultValues: {
      orgName: "",
      hajocaCustomerId: "",
      hajocaProfitCenter: "",
      phone: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "US",
        zipCode: "",
      },
    },
  });

  const [step, setStep] = useState<1 | 2>(1);
  const [lookupWarning, setLookupWarning] = useState<string | null>(null);

  const lookupMutation = useMutation({
    mutationFn: (customerId: string) => lookupHajocaCustomer(customerId),
    onSuccess: (result) => {
      setLookupWarning(null);
      if (result.data && result.data.length > 0) {
        const account = result.data[0];
        // Parse phone into E.164 format for PhoneField (default to US if no country code)
        const rawPhone = account.attributes.phone || "";
        const parsed = parsePhoneNumber(rawPhone, "US");
        const phone = parsed?.format("E.164") || rawPhone;
        form.setValue("phone", phone);
        form.setValue("address.street", account.attributes.address.addressLine1 || "");
        form.setValue("address.city", account.attributes.address.city || "");
        form.setValue("address.state", account.attributes.address.state || "");
        form.setValue("address.zipCode", account.attributes.address.zipCode || "");
      }
      setStep(2);
    },
    onError: () => {
      setLookupWarning("Could not fetch customer data — you can fill in fields manually.");
      setStep(2);
    },
  });

  const handleLookupAndContinue = async () => {
    const isValid = await form.trigger(["orgName", "hajocaCustomerId", "hajocaProfitCenter"]);
    if (!isValid) return;
    lookupMutation.mutate(form.getValues("hajocaCustomerId"));
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateContractorRequest) => createContractor(data),
    onSuccess: (result: CreateContractorResult) => {
      toast({
        title: "Organization created successfully",
        description: `"${result.clerkOrgName}" - Customer ID: ${result.supabaseOrg.hajocaCustomerId}, Profit Center: ${result.supabaseOrg.hajocaProfitCenter}`,
      });
      form.reset();
      onSuccess?.();
    },
    onError: (error) => {
      toastApiError(toast, error, "Failed to create organization");
    },
  });

  const onSubmit = (data: CreateContractorFormData) => {
    const cleanedData: CreateContractorRequest = {
      orgName: data.orgName,
      hajocaCustomerId: data.hajocaCustomerId,
      hajocaProfitCenter: data.hajocaProfitCenter,
      ...(data.phone && { phone: data.phone }),
      ...(data.address &&
        (data.address.street ||
          data.address.city ||
          data.address.state ||
          data.address.zipCode) && {
          address: {
            ...(data.address.street && { street: data.address.street }),
            ...(data.address.city && { city: data.address.city }),
            ...(data.address.state && { state: data.address.state }),
            ...(data.address.zipCode && { zipCode: data.address.zipCode }),
            country: data.address.country || "US",
          },
        }),
    };

    createMutation.mutate(cleanedData);
  };

  return (
    <div className="space-y-6">
      {createMutation.isSuccess && createMutation.data && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Organization Created Successfully!</AlertTitle>
          <AlertDescription className="text-green-700">
            <div className="mt-2 space-y-2">
              <div className="font-semibold text-green-800">
                {createMutation.data.clerkOrgName}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-green-600">Clerk Org ID:</span>{" "}
                  <span className="font-mono">{createMutation.data.clerkOrgId}</span>
                </div>
                <div>
                  <span className="text-green-600">Customer ID:</span>{" "}
                  <span className="font-mono">{createMutation.data.supabaseOrg.hajocaCustomerId}</span>
                </div>
                <div>
                  <span className="text-green-600">Profit Center:</span>{" "}
                  <span className="font-mono">{createMutation.data.supabaseOrg.hajocaProfitCenter}</span>
                </div>
                {createMutation.data.supabaseOrg.phone && (
                  <div>
                    <span className="text-green-600">Phone:</span>{" "}
                    <span>{createMutation.data.supabaseOrg.phone}</span>
                  </div>
                )}
              </div>
              {createMutation.data.supabaseOrg.addressLine1 && (
                <div className="text-sm">
                  <span className="text-green-600">Address:</span>{" "}
                  <span>
                    {createMutation.data.supabaseOrg.addressLine1}
                    {createMutation.data.supabaseOrg.city && `, ${createMutation.data.supabaseOrg.city}`}
                    {createMutation.data.supabaseOrg.state && `, ${createMutation.data.supabaseOrg.state}`}
                    {createMutation.data.supabaseOrg.zipCode && ` ${createMutation.data.supabaseOrg.zipCode}`}
                  </span>
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {createMutation.isError && (
        <Alert className="bg-red-50 border-red-200">
          <XCircle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Failed to Create Organization</AlertTitle>
          <AlertDescription className="text-red-700">
            <div className="mt-2 space-y-2">
              <div className="text-sm">
                {userErrorMessage(parseApiError(createMutation.error))}
              </div>
              <div className="text-xs text-red-600">
                Please check the information and try again. If the problem persists, contact support.
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Required Information</CardTitle>
              <CardDescription>
                Basic information needed to create the contractor organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name *</Label>
                <Input
                  id="orgName"
                  {...form.register("orgName")}
                  placeholder="Acme Plumbing Co."
                />
                {form.formState.errors.orgName && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.orgName.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hajocaCustomerId">Hajoca Customer ID *</Label>
                  <Input
                    id="hajocaCustomerId"
                    {...form.register("hajocaCustomerId")}
                    placeholder="W178156"
                  />
                  {form.formState.errors.hajocaCustomerId && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.hajocaCustomerId.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hajocaProfitCenter">Profit Center *</Label>
                  <Input
                    id="hajocaProfitCenter"
                    {...form.register("hajocaProfitCenter")}
                    placeholder="125"
                  />
                  {form.formState.errors.hajocaProfitCenter && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.hajocaProfitCenter.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Will be a dropdown in a future update
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            type="button"
            className="w-full"
            onClick={handleLookupAndContinue}
            disabled={lookupMutation.isPending}
          >
            {lookupMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Looking up customer...
              </>
            ) : (
              "Lookup & Continue"
            )}
          </Button>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {lookupWarning && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {lookupWarning}
              </AlertDescription>
            </Alert>
          )}

          <div className="rounded-lg border bg-muted/30 px-4 py-3">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Organization:</span>{" "}
                <span className="font-medium">{form.getValues("orgName")}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Customer ID:</span>{" "}
                <span className="font-mono">{form.getValues("hajocaCustomerId")}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Profit Center:</span>{" "}
                <span className="font-mono">{form.getValues("hajocaProfitCenter")}</span>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                {lookupWarning
                  ? "Enter the contact details manually"
                  : "Verify the contact details below (auto-filled from Hajoca)"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneField
                  value={form.watch("phone") || ""}
                  onChange={(value) => form.setValue("phone", value)}
                />
                {form.formState.errors.phone && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.phone.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.street">Street Address</Label>
                <Input
                  id="address.street"
                  {...form.register("address.street")}
                  placeholder="123 Main St"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address.city">City</Label>
                  <Input
                    id="address.city"
                    {...form.register("address.city")}
                    placeholder="Antioch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address.state">State</Label>
                  <StateSelect
                    value={form.watch("address.state")}
                    onValueChange={(value) => form.setValue("address.state", value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.zipCode">ZIP Code</Label>
                <MaskedInput
                  id="address.zipCode"
                  mask="zipcode"
                  {...form.register("address.zipCode")}
                  placeholder="94509"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Organization...
                </>
              ) : (
                "Create Contractor Organization"
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
